import {ChangeDetectionStrategy, Component, computed, input} from '@angular/core';
import {TileComponent} from '../tile/tile.component';
import {Tile} from '../../models/tile';
import {TileGrid} from '../../models/tile-grid';

/**
 * Lays a {@link TileGrid} out with CSS grid areas: every tile becomes a named area, so a tile can
 * span cells (repeat its id) and a cell can be left empty (name no tile) just by editing the
 * template — no positioning on the tiles themselves.
 */
@Component({
  selector: 'app-tile-grid',
  imports: [TileComponent],
  templateUrl: './tile-grid.component.html',
  styleUrl: './tile-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TileGridComponent {
  readonly grid = input.required<TileGrid>();

  /** Forwarded to every tile: make the whole card a link instead of showing a corner arrow. */
  readonly clickableTile = input(false);

  private readonly tileIds = computed<string[]>(() => Object.keys(this.grid().tiles));

  /** The tiles to render: those the template actually places. */
  protected readonly placedTileIds = computed<string[]>(() =>
    this.tileIds().filter(id => this.grid().template.flat().includes(id))
  );

  /**
   * The area map. A cell naming a tile that is not in `tiles` becomes `.` — an unnamed area — so a
   * template can leave gaps, and a grid whose data has not all arrived still lays out.
   */
  protected readonly gridTemplateAreas = computed<string>(() =>
    this.grid().template
      .map(row => `'${row.map(area => this.tileIds().includes(area) ? area : '.').join(' ')}'`)
      .join('\n')
  );

  /** Equal columns, one per cell in the first template row. */
  protected readonly gridTemplateColumns = computed<string>(() =>
    this.grid().template[0].map(() => '1fr').join(' ')
  );

  protected readonly gridTemplateRows = computed<string>(() =>
    this.grid().template.map(() => '1fr').join(' ')
  );

  protected getTile(id: string): Tile {
    return this.grid().tiles[id];
  }
}
